import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  requestNotificationPermission, 
  isNotificationEnabled, 
  getNotificationPermission 
} from '../utils/notificationUtils';

const NotificationToggle = ({ className = "", showLabel = true }) => {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsSupported('Notification' in window);
    setPermission(getNotificationPermission());
  }, []);

  const handleToggle = async () => {
    if (!isSupported) {
      toast.error('Notifications not supported in this browser');
      return;
    }

    if (permission === 'granted') {
      // Can't programmatically disable, show instructions
      toast.info('To disable notifications, click the 🔒 icon in your browser\'s address bar and block notifications.');
      return;
    }

    if (permission === 'denied') {
      toast.error('Notifications are blocked. Click the 🔒 icon in your browser\'s address bar to allow notifications.');
      return;
    }

    // Request permission
    setIsLoading(true);
    try {
      const granted = await requestNotificationPermission();
      
      if (granted) {
        setPermission('granted');
        toast.success('🔔 Notifications enabled! You\'ll now receive chat notifications.');
        
        // Show test notification
        setTimeout(() => {
          new Notification('🎉 VestrioZ Notifications Enabled', {
            body: 'You\'ll now receive notifications for new messages and inquiries.',
            icon: '/images/VESTRIOZ.png',
            tag: 'notification-enabled'
          });
        }, 500);
      } else {
        setPermission('denied');
        toast.error('❌ Notification permission denied. You can enable it later in your browser settings.');
      }
    } catch (error) {
      toast.error('Error setting up notifications');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  const getButtonStyle = () => {
    switch (permission) {
      case 'granted':
        return {
          bg: 'bg-green-500 hover:bg-green-600',
          text: 'text-white',
          icon: 'fa-bell',
          label: 'Notifications On'
        };
      case 'denied':
        return {
          bg: 'bg-red-500 hover:bg-red-600',
          text: 'text-white',
          icon: 'fa-bell-slash',
          label: 'Notifications Blocked'
        };
      default:
        return {
          bg: 'bg-yellow-500 hover:bg-yellow-600',
          text: 'text-white',
          icon: 'fa-bell',
          label: 'Enable Notifications'
        };
    }
  };

  const style = getButtonStyle();

  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      className={`${style.bg} ${style.text} px-3 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 shadow-lg ${className}`}
      title={
        permission === 'granted' ? 'Notifications are enabled' :
        permission === 'denied' ? 'Notifications are blocked - click to see instructions' :
        'Click to enable notifications'
      }
    >
      {isLoading ? (
        <i className="fas fa-circle-notch fa-spin"></i>
      ) : (
        <i className={`fas ${style.icon}`}></i>
      )}
      
      {showLabel && (
        <span className="text-sm font-semibold">
          {isLoading ? 'Setting up...' : style.label}
        </span>
      )}
      
      {permission === 'granted' && (
        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
      )}
    </button>
  );
};

export default NotificationToggle;